import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";
import GeofenceReportsTable from "./geofenceTripReportTable";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange"; // ✅ Imported Custom DateRangePicker
import DhundhooLoader from "../Loader/Loader";

function GeofenceTripReport() {
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  // ✅ Set to null by default so the "Select date range" placeholder displays
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Columns for DataTable
  const columns = [
    { Header: "SR NO", accessor: "srno", width: "5%" },
    { Header: "Vehicle", accessor: "thingName", width: "15%" },
    { Header: "Geofence Name", accessor: "fenceName", width: "20%" },
    { Header: "Entry Time", accessor: "entryTime", width: "20%" },
    { Header: "Exit Time", accessor: "exitTime", width: "20%" },
    { Header: "Duration Inside", accessor: "duration", width: "10%" },
  ];

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const fetchReport = async () => {
    // ✅ Validation to ensure dates are picked
    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first.");
      setErrorSB(true);
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    // ✅ New Formatter: Converts "YYYY-MM-DDTHH:mm" to API format "YYYY/MM/DD HH:mm"
    const formatDateTime = (isoString) => {
      const [d, t] = isoString.split("T");
      return `${d.replace(/-/g, "/")} ${t}`;
    };

    try {
      const response = await axios.post(
        "https://v3.dhundhoo.com/reports/geofenceReport",
        {
          startTime: formatDateTime(startTime),
          endTime: formatDateTime(endTime),
          zone: "Asia/Kolkata",
          things: vehicleIds.join(","),
          thingIds: vehicleIds,
        },
        {
          headers: {
            "x-dhundhoo-session": token,
            "Content-Type": "application/json",
          },
        }
      );

      // Map the new API response structure
      const rows = response.data.map((log, index) => ({
        srno: index + 1, // Generate a Serial Number
        thingName: log.thingName || "-",
        fenceName: log.fenceName || "-",
        entryTime: log.dateRange && log.entryTime ? `${log.dateRange} ${log.entryTime}` : "-",
        exitTime: log.dateRange && log.exitTime ? `${log.dateRange} ${log.exitTime}` : "-",
        duration: log.timeInside ? formatTime(log.timeInside) : "00:00:00",
      }));

      setReportData(rows);

      setReportData(rows);
      setSuccessSB(true);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to load geofence trip report");
      setErrorSB(true);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData.length) {
      setErrorMessage("No data to export");
      setErrorSB(true);
      return;
    }

    const formatFileDate = (dateTime) => {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formattedDate = `${formatFileDate(startTime)}_to_${formatFileDate(endTime)}`;

    // ✅ Updated CSV Headers
    const headers = [
      "SR NO",
      "Vehicle",
      "Geofence Name",
      "Entry Time",
      "Exit Time",
      "Duration Inside"
    ];

    // ✅ Updated CSV Rows Mapping
    const rows = reportData.map((r) =>
      [
        r.srno,
        r.thingName,
        r.fenceName,
        r.entryTime,
        r.exitTime,
        r.duration
      ].join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = `Geofence_Trip_Report_${formattedDate}.csv`;
    link.click();
  };

  const clearFilters = () => {
    setVehicleIds([]);
    setReportData([]);
    setErrorMessage("");
    setStartTime(defaultStart); // ✅ Reset back to today's date
    setEndTime(defaultEnd);
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>

                    {/* Vehicle Multi-Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        multiple
                        options={things || []}
                        getOptionLabel={(option) => option.thingId}
                        value={things?.filter((t) => vehicleIds.includes(t.thingId)) || []}
                        onChange={(event, newValue) => {
                          setVehicleIds(newValue.map((v) => v.thingId));
                        }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            <span>{option.nickName}</span>
                            <span style={{ color: "#666" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select Vehicles" fullWidth size="small" sx={{
                            "& .MuiOutlinedInput-root": {
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#1398DC", // ✅ border on click
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#1398DC", // ✅ label color (if label used)
                            },
                          }} />
                        )}
                      />
                    </Grid>

                    {/* ✅ Replaced dual DateTimePickers with single DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="gradient" color="info" size="small" onClick={fetchReport} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                        CLEAR
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading} />
                    </Grid>

                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {/* Error Alert */}
              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* Loader or Table */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <DhundhooLoader />
                ) : (
                  <GeofenceReportsTable
                    table={{ columns, rows: reportData }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <MDSnackbar color="success" icon="check" title="Geofence Trip Report" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
      <MDSnackbar color="error" icon="warning" title="Geofence Trip Report" content={errorMessage || "Failed to load report"} open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
    </DashboardLayout>
  );
}

export default GeofenceTripReport;